import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { CreditCard, Download, Info, ExternalLink, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
interface Invoice {
  id: string;
  date: string;
  description: string;
  amount: string;
  status: 'Paid' | 'Pending' | 'Failed';
}
export function SettingsAccountBillingPage() {
  const navigate = useNavigate();
  // Format date to DD/MM/YYYY
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  // Mock data - Extended billing history
  const [allInvoices] = useState<Invoice[]>([{
    id: '1',
    date: '2025-12-15',
    description: 'Growth Plan - Monthly',
    amount: '$99.00',
    status: 'Paid'
  }, {
    id: '2',
    date: '2025-11-15',
    description: 'Growth Plan - Monthly',
    amount: '$99.00',
    status: 'Paid'
  }, {
    id: '3',
    date: '2025-10-15',
    description: 'Growth Plan - Monthly',
    amount: '$99.00',
    status: 'Paid'
  }, {
    id: '4',
    date: '2025-09-15',
    description: 'Growth Plan - Monthly',
    amount: '$99.00',
    status: 'Paid'
  }, {
    id: '5',
    date: '2025-08-15',
    description: 'Growth Plan - Monthly',
    amount: '$99.00',
    status: 'Paid'
  }, {
    id: '6',
    date: '2025-07-15',
    description: 'Growth Plan - Monthly',
    amount: '$99.00',
    status: 'Paid'
  }, {
    id: '7',
    date: '2025-06-15',
    description: 'Growth Plan - Monthly',
    amount: '$99.00',
    status: 'Paid'
  }, {
    id: '8',
    date: '2025-05-15',
    description: 'Growth Plan - Monthly',
    amount: '$99.00',
    status: 'Paid'
  }]);
  // Billing history state
  const [billingExpanded, setBillingExpanded] = useState(false);
  const displayedInvoices = billingExpanded ? allInvoices : allInvoices.slice(0, 3);
  const handleDownloadInvoice = (invoiceId: string) => {
    console.log('Downloading invoice:', invoiceId);
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-8883">
      {/* NavBar */}
      <div className="mb-6" data-id="element-8884">
        <NavBar tabs={[]} title="Account & Billing" subtitle="Manage your WinePik subscription plan and billing history" data-id="element-8885" />
      </div>

      {/* Main Content - Full Width Layout */}
      <div className="space-y-6" data-id="element-8886">
        {/* Section 1: Current Plan */}
        <Card padding="none" data-id="element-8887">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-8888">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8889">
              Current Plan
            </h2>
            <Button variant="secondary" data-id="element-8890">Change Plan</Button>
          </div>

          <div className="p-6" data-id="element-8891">
            <div className="mb-6" data-id="element-8892">
              <div className="flex items-center gap-3 mb-2" data-id="element-8893">
                <h3 className="text-[24px] font-semibold text-[#0EA5E9]" data-id="element-8894">
                  Growth Plan
                </h3>
                <Badge variant="success" data-id="element-8895">Active</Badge>
              </div>
              <p className="text-[18px] text-[#475569] mb-1" data-id="element-8896">$99/month</p>
              <p className="text-[14px] text-[#64748B] mb-3" data-id="element-8897">
                Billed monthly · Renews Jan 15, 2026
              </p>
              <p className="text-[14px] text-[#64748B]" data-id="element-8898">
                Unlimited subscriptions · 5 team members · Priority support
              </p>
            </div>

            {/* Info Banner */}
            <div className="p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md flex items-start gap-3" data-id="element-8899">
              <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-8900" />
              <div className="flex-1" data-id="element-8901">
                <p className="text-[14px] text-[#0369A1] mb-2" data-id="element-8902">
                  Your WinePik subscription is managed through the Shopify App
                  Store. To change your plan or update payment details, visit
                  your Shopify admin.
                </p>
                <a href="https://admin.shopify.com/settings/billing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-8903">
                  Manage in Shopify
                  <ExternalLink className="w-3.5 h-3.5" data-id="element-8904" />
                </a>
              </div>
            </div>
          </div>
        </Card>

        {/* Section 2: Billing History */}
        <Card padding="none" data-id="element-8905">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-8906">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8907">
              Billing History
            </h2>
            <button onClick={() => setBillingExpanded(!billingExpanded)} className="flex items-center gap-2 text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-8908">
              <span data-id="element-8909">{billingExpanded ? 'Show Less' : 'Show More'}</span>
              {billingExpanded ? <ChevronUp className="w-4 h-4" data-id="element-8910" /> : <ChevronDown className="w-4 h-4" data-id="element-8911" />}
            </button>
          </div>

          <div className="overflow-hidden transition-all duration-250 ease-in-out" data-id="element-8912">
            <div className="overflow-x-auto" data-id="element-8913">
              <table className="w-full" data-id="element-8914">
                <thead className="bg-[#F1F5F9]" data-id="element-8915">
                  <tr data-id="element-8916">
                    <th className="px-6 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8917">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8918">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8919">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8920">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8921">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-8922">
                  {displayedInvoices.map((invoice, index) => <tr key={invoice.id} className="hover:bg-[#F8FAFC] transition-colors h-[56px]" style={{
                  opacity: !billingExpanded && index === 2 ? 0.6 : 1
                }} data-id="element-8923">
                      <td className="px-6 py-4 text-[14px] text-[#475569]" data-id="element-8924">
                        {formatDate(invoice.date)}
                      </td>
                      <td className="px-6 py-4 text-[14px] text-[#0F172A]" data-id="element-8925">
                        {invoice.description}
                      </td>
                      <td className="px-6 py-4 text-[14px] font-semibold text-[#0F172A]" data-id="element-8926">
                        {invoice.amount}
                      </td>
                      <td className="px-6 py-4" data-id="element-8927">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-medium" style={{
                      backgroundColor: '#D1FAE5',
                      color: '#065F46'
                    }} data-id="element-8928">
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-8929">
                        <button onClick={() => handleDownloadInvoice(invoice.id)} className="text-[#64748B] hover:text-[#0EA5E9] transition-colors" title="Download invoice" data-id="element-8930">
                          <Download className="w-4 h-4" data-id="element-8931" />
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </Card>

        {/* Section 3: Payment Method */}
        <Card padding="none" data-id="element-8932">
          <div className="p-6 border-b border-[#E2E8F0]" data-id="element-8933">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8934">
              Payment Method
            </h2>
          </div>

          <div className="p-6" data-id="element-8935">
            <div className="flex items-center gap-3 mb-4" data-id="element-8936">
              <CreditCard className="w-6 h-6 text-[#475569]" data-id="element-8937" />
              <div className="flex-1" data-id="element-8938">
                <p className="text-[16px] font-medium text-[#0F172A]" data-id="element-8939">
                  •••• •••• •••• 4242
                </p>
                <p className="text-[14px] text-[#64748B] mt-0.5" data-id="element-8940">
                  Expires 08/2027
                </p>
              </div>
              <a href="https://admin.shopify.com/settings/payments" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-8941">
                Update in Shopify
                <ExternalLink className="w-3.5 h-3.5" data-id="element-8942" />
              </a>
            </div>

            <p className="text-[14px] text-[#64748B]" data-id="element-8943">
              Payment is processed securely through the Shopify App Store.
            </p>
          </div>
        </Card>

        {/* Section 4: Danger Zone */}
        <Card padding="none" className="border-[#FCA5A5]" data-id="element-8944">
          <div className="p-6 border-b border-[#FCA5A5] bg-[#FEF2F2]" data-id="element-8945">
            <h2 className="text-[20px] font-semibold text-[#B91C1C]" data-id="element-8946">
              Danger Zone
            </h2>
          </div>

          <div className="p-6 bg-[#FEF2F2]" data-id="element-8947">
            <div className="mb-4" data-id="element-8948">
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-8949">
                Cancel Account
              </h3>
              <p className="text-[14px] text-[#64748B] mb-4" data-id="element-8950">
                Permanently delete your WinePik account and all associated data.
                This action cannot be undone.
              </p>
              <Button variant="outline" className="border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white" data-id="element-8951">
                Cancel Account
              </Button>
            </div>
            <p className="text-[14px] text-[#DC2626] flex items-start gap-2" data-id="element-8952">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-8953" />
              You will lose access to all subscription data, customer history,
              and settings.
            </p>
          </div>
        </Card>
      </div>
    </div>;
}